<template>
    <div v-if="loaded" class="frame">
        <div id="toc">
            <h1>{{ $t("doc.tableOfContents") }}</h1>
            <nav>
                <ol>
                    <li v-for="(contentRef,idx) in contentsRef" :key="idx">
                        <router-link :to="{ name: $route.name, params : { id: contentRef.id } }">
                            {{ contentRef.title }}
                        </router-link>
                    </li>
                </ol>
            </nav>
        </div>
        <div :id="contentId" class="content" @scroll="onScrollUpdate">
        </div>
    </div>
    <div v-else class="frame">
        {{ $t("loading") + "&hellip;" }}
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { getManual } from "@/services/doc-services";

import { RouteLocationRaw } from "vue-router";

interface IDocRef {
    title: string;
    id: string;
    children: IDocRef[] | null;
}

const route = useRoute();
const router = useRouter();
const { locale } = useI18n();

// Tracked reactive state
const id = ref("");
const loaded = ref(false);
const reqNo = ref(0);
const contentsRef = ref<IDocRef[]>([]);

// Untracked per-instance state
let scrollTickId: number | undefined;
let replacingRoute = false;

const focusId = computed((): string => {
    const idParam = route.params.id;
    return idParam !== undefined ? "" + idParam : "";
});

const contentId = computed((): string => "content-" + id.value);

// Replaces @Watch("$route")
watch(route, () => {
    if (!loaded.value) { return; }

    const offset = getArticlePosition(focusId.value);
    if (!replacingRoute || (offset !== null && offset !== 0)) { scrollToFocusId(); }
});

// Replaces $watch("$i18n.locale") in beforeMount — reload manual on locale change
watch(locale, (newLocale: string, oldLocale: string) => {
    if (newLocale === oldLocale) { return; }
    startManualLoad(); // Reload doc on language change
});

function getArticlePosition(articleId: string): number | null {
    if (articleId.length < 1) { return null; }

    const el = document.getElementById(articleId);
    if (el === null) { return null; }

    const rect = el.getBoundingClientRect();
    const limit = window.innerHeight * 0.2;
    let offset = 0;
    if (rect.top < 0 && rect.bottom < limit) {
        offset = -1; // Article is further up
    } else if (rect.top > (window.innerHeight - limit)) {
        offset = 1; // Article is further down
    }

    return offset;
}

function doTriggerScroll(offset: number): void {
    const existing = focusId.value;
    const idx = contentsRef.value.findIndex(c => c.id === existing);
    if (idx < 0) { return; }

    let possible: IDocRef[] | null = null;
    if (offset > 0) { // Current article is now further down
        // Find the next one behind
        possible = contentsRef.value.slice(0, idx).reverse();
    } else if (offset < 0) { // Current article has now been passed
        // Find the next one forward
        possible = contentsRef.value.slice(idx + 1);
    }

    if (possible === null) { return; }

    const matchRefIdx = possible.findIndex(c => getArticlePosition(c.id) === 0);
    if (matchRefIdx >= 0) {
        const location: RouteLocationRaw = {
            name: "docs",
            params: { id: possible[matchRefIdx].id }
        };
        replacingRoute = true;
        router.replace(location).finally(() => {
            replacingRoute = false;
        });
    }
}

function onScrollUpdate(): void {
    if (typeof scrollTickId !== "undefined") { return; }

    scrollTickId = window.setTimeout(() => {
        const currentFocusId = focusId.value;
        if (currentFocusId.length > 0) {
            const offset = getArticlePosition(currentFocusId);
            if (offset !== null) {
                if (offset !== 0) {
                    doTriggerScroll(offset);
                }
            }
        }

        scrollTickId = undefined;
    }, 150);
}

function scrollToFocusId(): void {
    let currentFocusId = focusId.value;
    if (currentFocusId.length < 1) { currentFocusId = "toc"; }
    const el = document.getElementById(currentFocusId);
    if (el === null) { return; }
    el.scrollIntoView();
}

function startManualLoad(): void {
    const expectedReqNo = reqNo.value + 1;
    reqNo.value = expectedReqNo;

    const promise = getManual(locale.value);
    promise.then(resp => {
        if (reqNo.value !== expectedReqNo) { return; } // No longer valid

        displayManual(resp.data);
    });
}

function displayManual(loadedDoc: Document): void {
    loaded.value = true;

    const articleNodes: HTMLElement[] = [];
    loadedDoc.querySelectorAll("article").forEach(el => {
        articleNodes.push(el.cloneNode(true) as HTMLElement);
    });

    contentsRef.value = articleNodes.map(articleEl => {
        if (articleEl.id.length < 1 || articleEl.firstElementChild == null) { return null; }

        const docRef: IDocRef = {
            title: articleEl.firstElementChild.textContent,
            id: articleEl.id,
            children: null
        };

        return docRef;
    }).filter(dr => dr !== null);

    if (articleNodes.length > 0) {
        window.setTimeout(() => {
            const mountEl = document.getElementById(contentId.value);
            while (mountEl.hasChildNodes()) { mountEl.removeChild(mountEl.lastChild); }

            articleNodes.forEach(el => mountEl.appendChild(el));

            if (focusId.value.length > 0) {
                scrollToFocusId();
            } else {
                if (contentsRef.value.length > 0) {
                    const firstId = contentsRef.value[0].id;
                    if (getArticlePosition(firstId) === 0) {
                        const location: RouteLocationRaw = { name: "docs", params: { id: firstId } };
                        router.replace(location);
                    }
                }
            }
        }, 10);
    }
}

onMounted(() => {
    const arr = new Uint32Array(1);
    window.crypto.getRandomValues(arr);
    id.value = "frame-" + arr[0].toString(16).slice(2, 8);

    startManualLoad();
});

onBeforeUnmount(() => {
    if (typeof scrollTickId !== "undefined") {
        window.clearTimeout(scrollTickId);
        scrollTickId = undefined;
    }
});
</script>

<style scoped>
.frame {
    margin: 0;
}

#toc > h1 {
    margin: .25em;
}

ol {
    padding-left: 1em;
}

ol > li {
    list-style: none;
}

ol {
    line-height: 1.5em;
    margin-top: 0;
    margin-bottom: 0;
}

ol > li > a {
    text-decoration: none;
    color: var(--ink-colour);
    user-select: none;
}

ol > li > a:hover {
    font-weight: bold;
    position: relative;
}

ol > li > a:hover::before {
    content: '|';
    position: absolute;
    left: -.3em;
    top: -.3em;
}

@media screen and (max-width: 799px) {
    .frame {
        display: block;
        height: calc(var(--body-size) - .5em);
        overflow-y: auto;
    }

    .content {
        margin: 2px;
    }
}

@media screen and (min-width: 800px) {
    .frame {
        display: grid;
        gap: .5em;
    }

    #toc {
        grid-column: 1;
        overflow-y: auto;

        max-width: max(15vw, 18em);
        height: calc(var(--body-size) - .5em);
        padding-right: .5em;
    }

    ol > li > a.router-link-active {
        text-decoration: none;
        cursor: default;
        font-weight: bold;
    }

    ol > li:hover > a.router-link-active::before {
        content: unset;
        position: unset;
        margin-left: unset;
    }

    ol > li:hover > a.router-link-active::after {
        content: unset;
        position: unset;
    }

    .content {
        grid-column: 2;
        overflow-y: auto;

        height: calc(var(--body-size) - .5em);
    }
}
</style>

<style>
article > h1 {
    padding-top: .5em;
    width: 100%;
    border-top: 2px solid var(--ink-colour);
}

article:first-child > h1 {
    border-top: none;
}

article > h1, section > h1, aside > h1 {
    font-size: x-large;
}

article > h2, section > h2 {
    padding: .25em 0;
    border-top: 1px solid var(--ink-colour);
}

article > h2, section > h2, aside > h2 {
    font-weight: bold;
    font-size: large;
    margin: .5em 0 .5em 0;
}

aside > h2:first-child {
    margin: 0 0 .5em 0;
}

article > h3, section > h3 {
    padding: .25em 0 0 0;
}

article > h3, section > h3, aside > h3 {
    font-size: medium;
    margin: .25em 0;
}

aside > h3:first-child {
    margin: 0;
}

article > table > tbody > tr:nth-child(odd) {
    background-color: var(--outline-colour);
}

article > table > tbody > tr > th,
article > table > tbody > tr > td {
    border: 2px solid var(--selected-colour);
    text-align: center;
}

article > table > tbody > tr > th {
    min-width: 8em;
}

aside {
    background-color: var(--hover-colour);
    padding: .5em;
    margin: 1em .25em 1em .25em;
}

aside > p:last-child {
    margin-bottom: 0;
}

aside > table > tbody > tr {
    background-color: var(--bg-colour);
}

aside > table > tbody > tr > td:first-child {
    min-width: min(12em, 45vw);
}

.key {
    display: inline-block;
    font-family: var(--monospace-font);
    margin: 0 .2em;
    padding: .2em .4em;
    background-color: var(--outline-colour);
    border: 1px solid var(--focus-colour);
    vertical-align: baseline;
}

.key.arrow {
    font-family: var(--base-font);
    min-width: 1em;
    text-align: center;
}

.mouse-action {
    display: inline-block;
    background-color: var(--bg-colour);
    border: 1px solid var(--focus-colour);
    padding: .2em .5em;
    margin: .2em;
    font-size: smaller;
}
</style>
